import { useCallback } from 'react';
import type { Cell, Rect, UndoAction } from '../types';
import { apiService, CustomFieldValue } from '../../../services/api';

// Make TSV from selection
export const makeTSV = (getVal: (r: number, c: string) => string, rect: Rect): string => {
  const rows: string[] = [];
  for (let r = rect.r0; r <= rect.r1; r++) {
    const cells: string[] = [];
    for (let ci = rect.c0; ci <= rect.c1; ci++) {
      const colKey = rect.colOrder[ci];
      cells.push(getVal(r, colKey));
    }
    rows.push(cells.join('\t'));
  }
  return rows.join('\n');
};

// Parse TSV into grid data
export const parseTSV = (tsv: string): string[][] => {
  return tsv.split('\n').map(row => row.split('\t'));
};

interface CustomField {
  id: string;
  name: string;
  type: string;
}

interface Founder {
  id: string;
  customFieldValues?: CustomFieldValue[];
  [key: string]: any;
}

interface UseClipboardProps {
  activeCell: Cell | null;
  selectionRect: Rect | null;
  colOrder: string[];
  filteredFounders: Founder[];
  customFields: CustomField[];
  getVal: (row: number, colKey: string) => string;
  handleFounderUpdate: (founderId: string, updates: any) => void;
  addUndoAction: (action: UndoAction) => void;
}

const FIELD_MAP: Record<string, string> = {
  email: 'email',
  company: 'company',
  position: 'roleTitle',
  linkedinUrl: 'profileUrl',
  industry: 'industry',
  location: 'location',
  stage: 'stage',
  status: 'status',
  tag: 'tag',
};

const DISPLAY_FIELD_MAP: Record<string, string> = {
  position: 'roleTitle',
  linkedinUrl: 'profileUrl',
};

export function useClipboard({
  activeCell,
  selectionRect,
  colOrder,
  filteredFounders,
  customFields,
  getVal,
  handleFounderUpdate,
  addUndoAction,
}: UseClipboardProps) {
  // Helper to check if a column is a custom field
  const isCustomFieldColumn = useCallback((colKey: string): boolean => {
    return customFields.some(field => field.id === colKey);
  }, [customFields]);

  // Copy handler
  const copy = useCallback(() => {
    // If single cell selected, copy just its value as plain text
    if (activeCell && (!selectionRect || (selectionRect.r0 === selectionRect.r1 && selectionRect.c0 === selectionRect.c1))) {
      const value = getVal(activeCell.row, activeCell.colKey);
      navigator.clipboard.writeText(value).catch(error => {
        console.warn('Clipboard write access denied:', error);
        alert('Please allow clipboard access in your browser settings to use copy functionality.');
      });
      return;
    }

    // Otherwise, copy as TSV for multi-cell selection
    if (!selectionRect) {
      console.warn('[useClipboard] Copy called but no selection');
      return;
    }
    const tsv = makeTSV(getVal, selectionRect);
    navigator.clipboard.writeText(tsv).catch(error => {
      console.warn('Clipboard write access denied:', error);
      alert('Please allow clipboard access in your browser settings to use copy functionality.');
    });
  }, [activeCell, selectionRect, getVal]);

  // Helper to paste to single cell
  const pasteSingleCell = useCallback(async (row: number, colKey: string, value: string) => {
    const founder = filteredFounders[row];
    if (!founder) return;

    const oldValue = getVal(row, colKey);

    // Handle custom field
    if (isCustomFieldColumn(colKey)) {
      // Optimistic update: update local state immediately
      const currentValues = founder.customFieldValues || [];
      const existingEntry = currentValues.find((cfv: CustomFieldValue) => cfv.fieldId === colKey);
      let updatedValues: CustomFieldValue[];
      if (existingEntry) {
        updatedValues = currentValues.map((cfv: CustomFieldValue) =>
          cfv.fieldId === colKey ? { ...cfv, value } : cfv
        );
      } else {
        // Create a minimal CustomFieldValue for display purposes
        updatedValues = [...currentValues, {
          id: `temp-${Date.now()}`,
          fieldId: colKey,
          value,
          founderId: founder.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          field: {} as any, // Will be populated on next fetch
        }];
      }

      // Apply optimistic update
      handleFounderUpdate(founder.id, { customFieldValues: updatedValues });

      // Record undo action
      addUndoAction({
        type: 'paste',
        founderId: founder.id,
        colKey,
        oldValue,
        newValue: value,
        isCustomField: true,
      });

      // Save to API (fire and forget for now, could add error rollback)
      try {
        await apiService.setCustomFieldValue(colKey, founder.id, value);
      } catch (error) {
        console.error(`Error pasting to custom field ${colKey}:`, error);
        // Rollback on error
        handleFounderUpdate(founder.id, { customFieldValues: currentValues });
      }
      return;
    }

    // Handle built-in field
    const apiField = FIELD_MAP[colKey];
    if (!apiField) return;

    try {
      await apiService.updateFounder(founder.id, { [apiField]: value } as any);
      addUndoAction({
        type: 'paste',
        founderId: founder.id,
        colKey,
        oldValue,
        newValue: value,
        isCustomField: false,
      });

      const displayField = DISPLAY_FIELD_MAP[colKey] || colKey;
      handleFounderUpdate(founder.id, { [displayField]: value } as any);
    } catch (error) {
      console.error(`Error pasting to ${colKey}:`, error);
    }
  }, [filteredFounders, getVal, isCustomFieldColumn, addUndoAction, handleFounderUpdate]);

  // Paste handler
  const paste = useCallback(async (text: string) => {
    if (!activeCell && !selectionRect) {
      console.warn('[useClipboard] paste() called but no activeCell or selectionRect');
      return;
    }

    // Use active cell or first cell of selection
    const effectiveCell: Cell | null = activeCell || (selectionRect ? {
      row: selectionRect.r0,
      colKey: selectionRect.colOrder[selectionRect.c0],
    } : null);

    if (!effectiveCell) return;

    // Check if it's a simple single value
    const isNotesColumn = effectiveCell.colKey === 'notes';
    const isSingleValue = (!text.includes('\t') && !text.includes('\n') && (!text.includes(',') || text.split(',').length <= 1)) ||
                          (isNotesColumn && !text.includes('\t'));

    if (isSingleValue) {
      // Check if we have a multi-cell selection
      if (selectionRect && (selectionRect.r0 !== selectionRect.r1 || selectionRect.c0 !== selectionRect.c1)) {
        // Paste the same value to all selected cells
        for (let r = selectionRect.r0; r <= selectionRect.r1; r++) {
          for (let ci = selectionRect.c0; ci <= selectionRect.c1; ci++) {
            const colKey = selectionRect.colOrder[ci];
            await pasteSingleCell(r, colKey, text);
          }
        }
      } else {
        // Simple single-cell paste
        await pasteSingleCell(effectiveCell.row, effectiveCell.colKey, text);
      }
      return;
    }

    // Multi-cell paste (TSV/CSV)
    const grid = parseTSV(text);
    for (let i = 0; i < grid.length; i++) {
      for (let j = 0; j < grid[i].length; j++) {
        const r = effectiveCell.row + i;
        const cIdx = colOrder.indexOf(effectiveCell.colKey) + j;
        if (r >= filteredFounders.length || cIdx >= colOrder.length) continue;
        await pasteSingleCell(r, colOrder[cIdx], grid[i][j]);
      }
    }
  }, [activeCell, selectionRect, colOrder, filteredFounders.length, pasteSingleCell]);

  return {
    copy,
    paste,
    makeTSV,
    parseTSV,
  };
}
